import React from 'react';

export interface Goal {
  label: string;
  current: number;
  target: number;
  unit?: string;
  color?: string;
}

export interface GoalProgressProps {
  goals?: Goal[];
  isLoading?: boolean;
}

const defaultGoals: Goal[] = [
  {
    label: 'Monthly Revenue',
    current: 45000,
    target: 60000,
    unit: '€',
    color: 'bg-blue-500',
  },
  {
    label: 'New Clients',
    current: 12,
    target: 20,
    color: 'bg-green-500',
  },
  {
    label: 'Tasks Completed',
    current: 85,
    target: 100,
    color: 'bg-teal-500',
  },
];

const GoalProgress: React.FC<GoalProgressProps> = ({ goals = defaultGoals, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Goal Progress</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item}>
              <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-2"></div>
              <div className="h-2 bg-gray-200 animate-pulse rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const formatValue = (value: number, unit?: string) => {
    if (unit === '€') {
      return `€${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return value.toString();
  };

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Goal Progress</h2>
      <div className="space-y-4 sm:space-y-6">
        {goals.map((goal, index) => {
          const percentage = Math.min((goal.current / goal.target) * 100, 100);
          const color = goal.color || 'bg-teal-500';
          
          return (
            <div key={index}>
              <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                <span className="text-xs sm:text-sm font-medium text-gray-700 truncate pr-2">{goal.label}</span>
                <span className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {formatValue(goal.current, goal.unit)} / {formatValue(goal.target, goal.unit)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
                <div
                  className={`${color} h-2 sm:h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-1 sm:pr-2`}
                  style={{ width: `${percentage}%` }}
                >
                  {percentage >= 10 && (
                    <span className="text-[10px] sm:text-xs text-white font-medium">{percentage.toFixed(0)}%</span>
                  )}
                </div>
              </div>
              {percentage < 10 && (
                <div className="text-right mt-0.5 sm:mt-1">
                  <span className="text-[10px] sm:text-xs text-gray-500">{percentage.toFixed(1)}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GoalProgress;

